import { Banner } from '@astryxdesign/core/Banner';
import { Button } from '@astryxdesign/core/Button';
import { Center } from '@astryxdesign/core/Center';
import { Skeleton } from '@astryxdesign/core/Skeleton';
import { VStack } from '@astryxdesign/core/VStack';
import { useEffect } from 'react';
import { Outlet, useParams } from 'react-router';
import { useStudio } from '@/api/public';
import { AppProviders } from '@/app/AppProviders';
import { DEFAULT_ACCENT } from '@/app/theme';
import { ErrorView } from '@/components/StateViews';
import { errorCode } from '@/lib/errors';
import { registerStudioWorker } from '@/lib/pwa';
import { StudioContext } from './StudioContext';

/** Loads the studio by slug and provides its data + accent theme to /s/:slug/**. */
export function StudioRoot() {
  const { slug = '' } = useParams();
  const q = useStudio(slug);

  useEffect(() => {
    void registerStudioWorker(slug);
  }, [slug]);

  useEffect(() => {
    if (q.data) document.title = q.data.settings.name;
  }, [q.data]);

  if (q.isPending) {
    return (
      <AppProviders accent={DEFAULT_ACCENT}>
        <VStack gap={4} aria-busy="true">
          <Skeleton height="62svh" radius="none" />
          <VStack gap={3} paddingInline={4}>
            <Skeleton height={32} width="70%" />
            <Skeleton height={20} width="90%" />
            <Skeleton height={120} />
          </VStack>
        </VStack>
      </AppProviders>
    );
  }

  if (q.isError || !q.data) {
    const notFound = errorCode(q.error) === 'studio_not_found';
    return (
      <AppProviders accent={DEFAULT_ACCENT}>
        <Center minHeight="100svh">
          <VStack gap={4} padding={6} maxWidth={420}>
            {notFound ? (
              <ErrorView error={q.error} />
            ) : (
              <ErrorView error={q.error} onRetry={() => void q.refetch()} />
            )}
            {notFound && <Button label="На главную" href="/" variant="ghost" />}
          </VStack>
        </Center>
      </AppProviders>
    );
  }

  return (
    <AppProviders accent={q.data.settings.accentColor}>
      <StudioContext.Provider value={q.data}>
        {q.data.isPreview && (
          <Banner
            status="info"
            container="section"
            title="Образец студии"
            description="Записи здесь демонстрационные, уведомления не отправляются."
            collapsible={false}
          />
        )}
        <Outlet />
      </StudioContext.Provider>
    </AppProviders>
  );
}
